/* @flow */
import { mapDimensions, LatLng, LeafletClickTarget, LeafletMouseEvent, Marker, THEME } from './constants';
import { MapStorage, UserStorage } from './storage';
import markers from './markers';
import pb from './pubsub';
import { deepEqual, granularity } from './utils';
import { type granuleSize } from '../common/types'

const pubSub = pb.setup('citymap')

// make flow aware of the libraries
/*::
import d3 from 'javascriptLibs'
import L from 'javascriptLibs'
import ui from 'javascriptLibs'
 */

const METERS_TO_MILES = 0.00062137;
let mapStore = new MapStorage();

const mapbox = ((document.getElementById('leaflet'): any): HTMLElement);
mapbox.style.width = mapDimensions.width + "px";
let mapView = L.map('leaflet').setView([51.505, -0.09], 13);
markers.setMapView(mapView);

let accessToken = 'pk.eyJ1IjoibWVsaGVvciIsImEiOiJjanVycDQ5a2UxZG1zNDRvMXdrb21wcGh3In0.I7JKZ6C6Nb2JR7jNM4j0kg';
const routeTooltip = ((document.getElementById('marker-tooltip'): any): HTMLElement);

const tooltipTitle = ((document.getElementById('state-tooltip-title'): any): HTMLElement);
const tooltipClose = ((document.getElementById('state-tooltip-close'): any): HTMLElement);

class CityMap {

    geoJson: Object;                                    // zoom-enhance object cached            
    geoJsonLayer: ?L.GeoJsonLayer;                      // leaflet layer for zipcode geoJson
    currentlyZoomedZip: ?string;                        // zipcode currently zoomed in
    _selectedZipcodeFeatureData: ?LeafletClickTarget;   // target zipcode of user focus (selection) on the map
    routeStart: ?{                                      // starting point of current route
        latLng: LatLng,
        xy: { x: number, y: number }
    };
    ruler: L.PolyLine;
    _granule: granuleSize;
    showHeatmap: boolean;
    _markerList: Marker[];

    constructor() {

        this.geoJson = null;
        this.geoJsonLayer = null;       
        this.routeStart = null;
        this.showHeatmap = true;

        // subscribed events

        // keep granule in-sync with dashboard
        this._granule = 'zip'
        pubSub.subscribe('setup:region', regionData => {
            // initial granule setuo data
            this._granule = regionData.granule
        })
        pubSub.subscribe('sync:store', data => {
            // granule has been updated by index (i.e. fixGranularity)
            this._granule = data.granule
        })
        pubSub.subscribe('zoom:granularity', val => {
            // granule has been updated by dashboard
            this._granule = val
        })

        // update geojson layer when new ranks come in
        pubSub.subscribe('ranks:appliedRegion', (data) => {
            // leaflet recomputation
            if (this.geoJsonLayer) {
                this.geoJsonLayer.setStyle(f => this.styleFeature(f));
            }

            // regenerate selection and tooltip
            if (this._selectedZipcodeFeatureData) {
                pubSub.publish('tooltip:zip', this._selectedZipcodeFeatureData.feature)
                this._selectedZipcodeFeatureData.setStyle(this.styleFeature(this._selectedZipcodeFeatureData.feature, true));
            }
        });

        // clean up leaflet-specific UI and reset/zoomout
        pubSub.subscribe(['zoom:reset', 'zoom:resetToState'], (noData, id, eventName) => {
            // cleanup layers
            mapbox.style.display = "";
            tooltipTitle.style.marginRight = "";
            tooltipClose.style.display = "";
            ((document.getElementById('leaflet-controls'): any): HTMLElement).style.display = ""; // hide citymap
            this._selectedZipcodeFeatureData = null;
            if (this.geoJsonLayer) {
                this.geoJsonLayer.eachLayer(layer => mapView.removeLayer(layer));
            }
            markers.cleanupMarkers();
            if (eventName === 'zoom:resetToState') {
                // don't load US map
                let canvas = ((document.getElementById('canvas'): any): HTMLCanvasElement)
                canvas.style.display = "none"
            }
            this.geoJsonLayer = null;
        });

        pubSub.subscribe('marker:preview', (marker) => {
            if (this.geoJsonLayer) {
                pubSub.publish('location:load', { obj: marker, type: 'marker' })
            }
        })

        // cleanup without zoomout/reset, I believe this happens when switching cities (don't remember anymmore)
        pubSub.subscribe('zoom:cleanup', () => {
            if (this.geoJsonLayer) {
                this.geoJsonLayer.eachLayer(layer => mapView.removeLayer(layer));
            }
            this._selectedZipcodeFeatureData = null;
            markers.cleanupMarkers();
        });

        const refreshGranules = () => {
            if (this.showHeatmap) {
                if (this.geoJsonLayer) {
                    this.geoJsonLayer.eachLayer(layer => mapView.removeLayer(layer));
                }
                this.geoJsonLayer = L.geoJson(this.geoJson, {
                    style: (f) => this.styleFeature(f), 
                    onEachFeature: (feature, layer) => {
                        // attach event handlers to zipcode regions
                        layer.on({
                            click: this.handleClick
                        })
                    }
                }).addTo(mapView);
            }
        }

        const showHeatmap = (show) => {
            this.showHeatmap = show
            if (this.geoJsonLayer) {
                if (show) {
                    this.geoJsonLayer = L.geoJson(this.geoJson, {
                        style: (f) => this.styleFeature(f), 
                        onEachFeature: (feature, layer) => {
                            // attach event handlers to zipcode regions
                            layer.on({
                                click: this.handleClick
                            })
                        }
                    }).addTo(mapView);
                } else {
                    this.geoJsonLayer.eachLayer(layer => mapView.removeLayer(layer));
                }
            }
        }
        pubSub.subscribe('layer:heatmap', showHeatmap)

        // update zipcode resolution when available, also used when repositioning marker or region
        pubSub.subscribe('zoom:enhance', (geoJson) => {
            if (!this.geoJson || !deepEqual(this.geoJson, geoJson)) {
                this.geoJson = geoJson.geo
                refreshGranules()
            }
        });
        pubSub.subscribe('zoom:map', refreshGranules);

        // apply correct selection graphic based on selected item (marker, zipcode, etc.)
        pubSub.subscribe('location:load', (loc) => {
            tooltipClose.style.display = "block";
            tooltipTitle.style.marginRight = "30px";
            if (['zip', 'county', 'tract'].includes(loc.type)) {
                // zipcode selected (reset any marker selection)
                document.querySelectorAll('.selected-pin').forEach(p => p.classList.remove('selected-pin'));
            } else {
                // marker selected (reset any zipcode selection)
                this.resetHighlight()
            }
        })

        // when granule size changes, reset any granule selection
        pubSub.subscribe('zoom:granularity', () => {
            this.resetHighlight()
        })

        // deselect location
        pubSub.subscribe('location:cancel', () => {
            this.resetHighlight()
            document.querySelectorAll('.selected-pin').forEach(p => p.classList.remove('selected-pin'))
        })

        // listen to measure events triggered from marker interaction
        pubSub.subscribe('measure:startMarker', (e: LeafletMouseEvent) => {
            this._startMeasure(e);
        });

        L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
            attribution: '<a href="https://mapbox.com/">MapBox</a>',
            tileSize: 512,
            maxZoom: 20,
            zoomOffset: -1,
            id: 'mapbox/streets-v11',
            accessToken: accessToken
        }).addTo(mapView);

        // change current layer
        const layers = {
            'street': null,
            'topology': { provider: 'Esri.WorldTopoMap' },
            'satellite': { provider: 'Esri.WorldImagery' },
            'transportation': { provider: 'Thunderforest.TransportDark', api: '3216c3ff34494377b360138927f31663' },
        }
        let currentLayer = null
        pubSub.subscribe('layer:map', (label) => {
            if (currentLayer) {
                mapView.removeLayer(currentLayer)
            }
            if (layers[label] === null) {
                return;
            }
            currentLayer = L.tileLayer.provider(layers[label].provider, layers[label].api ? { apikey: layers[label].api } : null)
            currentLayer.addTo(mapView);
            if (mapView.getZoom() > 18 && label !== 'transportation') {
                pubSub.publish('notify:error', 'This zoom level is higher than the selected layer can display. Zoom out to see the layer.')
            }
        })
        // L.tileLayer.provider('Esri.WorldTopoMap').addTo(mapView);

        // @flow-ignore: flow doesn't get binding syntax
        this.loadZipcodeMap = this.loadZipcodeMap.bind(this);
        // @flow-ignore: flow doesn't get binding syntax
        this.loadLocation = this.loadLocation.bind(this);
        // @flow-ignore: flow doesn't get binding syntax
        this.handleClick = this.handleClick.bind(this);
        // @flow-ignore: flow doesn't get binding syntax
        this.zoomToFeature = this.zoomToFeature.bind(this);

        addEventListener("resize", (event) => {
            mapbox.style.width = mapDimensions.width + "px";
            mapView.invalidateSize()
        })

        // load markers
        this._markerList = []
        markers.fetch().then(list => {
            list.forEach(marker => markers.loadMarkerLeaflet(marker));
            // remove selection class from every pin
            document.querySelectorAll('.selected-pin').forEach(p => p.classList.remove('selected-pin'));
            pubSub.establish('load:markers')
            pubSub.publish('load:markers', list);
            this._markerList = list;
        });
    }

    // set style of a given zipcode region
    getColor(granuleId: string) {
        // @flow-ignore: SVG element has style.fill
        const element = document.getElementById(granuleId + `_${this._granule}_state`)
        return element ? element.style.fill : null;
    }

    styleFeature(feature, highlight) {
        const color = this.getColor(granularity[this._granule].stateId(feature))
        const theme = highlight ? THEME.selected : color ? THEME.metric : THEME.plain
        return {
            fillColor: color,
            stroke: true,
            weight: theme.edge,
            opacity: 1,
            color: theme.color,
            dashArray: '3',
            fillOpacity: theme.opacity
        };
    }

    // load the map itself
    loadMap() {
        mapbox.style.display = "block";
        mapView.invalidateSize();
        this._markerList.forEach(marker => markers.loadMarkerLeaflet(marker));
        // remove selection class from every pin
        document.querySelectorAll('.selected-pin').forEach(p => p.classList.remove('selected-pin'));
    }

    // zooms to particular zipcode, triggers post-render callback, if any
    // TODO: check if we even need this method anymore, it may be completely obsoleted by loadRegion (may not do anything after that method runs)
    loadZipcodeMap(zipcode: Object, cb: Function, publishEvent: boolean = true) {
        if (this.geoJsonLayer) {
            this.geoJsonLayer.eachLayer(layer => {
                if (layer.feature.properties.ZCTA5CE10 === zipcode.properties.ZCTA5CE10) {
                    this.zoomToFeature(layer, cb, publishEvent); // fake call to trigger correct marker highlight
                }
            });
        }
        // ((document.getElementById('leaflet-controls'): any): HTMLElement).style.display = "block";
    }

    // preliminary step to give perception of better performance, zooms map to location before zipcode map is ready
    // uses GeoJSON region, note the flip of lng,lat to lat,lng (Leaflet and GeoJSON/d3 use different formats)
    loadRegion(region: [number, number][], cb: Function, retarget: boolean = false) {
        this.loadMap();
        // @flow-ignore: flow seems to struggle recognizing array of tuples
        if (!this._selectedZipcodeFeatureData || retarget) {
            mapView.fitBounds(L.latLngBounds(region[0].map(p => [p[1], p[0]])));
        }
        cb && cb();
        pubSub.publish('zoom:map', region); 
    }

    // zooms to particular coordinate, triggers post-render callback, if any
    loadLocation(latLng: LatLng, cb: Function, zoom?: number) {
        this.loadMap();
        mapView.setView([latLng.lat, latLng.lng], zoom);
        cb && cb();
        pubSub.publish('zoom:map', latLng);
        // ((document.getElementById('leaflet-controls'): any): HTMLElement).style.display = "block";
    }

    // TODO: deprecated? no longer firing, seem to be calling markers directly, bypassing this
    selectMarker(marker: Marker) {
        markers.createOrSelectMarker(marker.latLng, marker.zip)
    }

    // TODO: either deprecated or only used for creation
    // prefers to select a marker that's already there, but can create new one as needed
    createOrSelectMarker(latLng: LatLng, zip: string, tentativeName?: string) {
        //let zip = zipcode || this.findZip(latLng);
        if (!zip) {
            // shoudn't happen
            console.error(`Couldn't find zipcode for coords (lat: ${latLng.lat}, lng: ${latLng.lng})`);
        }
        markers.createOrSelectMarker(latLng, zip || '', tentativeName);
    }

    // resets any temproary tooltip states
    resetTooltip() {
        markers.resetTooltipCalendar();
    }

    _startMeasure(e: LeafletMouseEvent) {
        this.routeStart = {
            latLng: e.latlng,
            xy: e.containerPoint
        };
        this.ruler = L.polyline([e.latlng, e.latlng], {
            weight: 2,
        }).addTo(mapView);
        mapView.on('mousemove', this._moveMeasure, this);
        mapView.on('click', this._finishMeasure, this)
    }

    _moveMeasure(e: LeafletMouseEvent) {
        // @flow-ignore: routeStart will be set by startmeasure
        const start = this.routeStart.latLng;
        const end = e.latlng;
        // @flow-ignore: technically flow is right, this is Leaflet's LatLng, not ours
        const distance = start.distanceTo(end) * METERS_TO_MILES;
        if (this.ruler) {
            this.ruler.setLatLngs([start, end]);
            routeTooltip.innerHTML = `${distance.toFixed(2)} miles`;
            routeTooltip.style.display = "block";
            // @flow-ignore: routeStart will be set by startmeasure
            routeTooltip.style.left = ((this.routeStart.xy.x + e.containerPoint.x) / 2 - routeTooltip.offsetWidth / 2) + "px";
            // @flow-ignore: routeStart will be set by startmeasure
            routeTooltip.style.top = ((this.routeStart.xy.y + e.containerPoint.y) / 2) + "px";
        }
    }

    _finishMeasure(e: LeafletMouseEvent) {
        mapView.off('mousemove', this._moveMeasure, this);
        mapView.off('click', this._finishMeasure, this);
        if (this.ruler) {
            mapView.removeLayer(this.ruler);
            this.ruler = null;
            routeTooltip.style.display = "";
        }
        pubSub.publish('measure:finish', {
            // @flow-ignore: routeStart will be set by startmeasure
            start: this.routeStart.latLng,
            end: e.latlng
        });
    }

    // handles user click on a location on the map
    handleClick(e: LeafletMouseEvent) {
        const mode = markers.getMode();
        if (mode == 'marker') {
            let zip = this.findZip(e.latlng);
            if (!zip) {
                // shoudn't happen
                console.error(`Couldn't find zipcode for coords (lat: ${e.latlng.lat}, lng: ${e.latlng.lng})`);
            }
            markers.createMarker({
                latlng: e.latlng,
                zip: zip || ''
            });
        } else if (mode == 'route') {
            this._startMeasure(e);
        } else {
            this.zoomToFeature(e.target);
        }
    }

    // focus on target feature and set zoom level accordingly
    zoomToFeature(target: LeafletClickTarget, cb: Function, publishEvent: boolean = true) {
        // if user clicks already selected feature again, retarget view on it, otherwise we simply select it
        if (this._selectedZipcodeFeatureData === target) {
            let bounds = target.getBounds();
            mapView.flyToBounds(bounds);
        } else {
            console.log('zooming to feature', target.feature.properties.ZCTA5CE10)
            this.highlight(target)
        }

        pubSub.publish('tooltip:zip', target.feature)
        if (publishEvent) {
            // we may not always want to publish a new event, i.e. if load is a result of search that already contains a better name
            let feature = {
                type: this._granule,
                query: granularity[this._granule].stateId(target.feature),
            }
            pubSub.publish('location:load', feature);
        }
        cb && cb()

        // load transit stops
        /*let props = target.feature.properties;
        let radius = bounds._northEast.distanceTo(bounds._southWest) / 2;
        fetch(`https://transit.land/api/v1/stops.geojson?lat=${props.INTPTLAT10}&lon=${props.INTPTLON10}&r=${radius}&total=true`).then(response => {
            response.json().then(data => {
                let transitData = L.geoJson(data, {
                    //                style: styleFeature, 
                    //                onEachFeature: onEachFeature
                }).addTo(mapView);
            });
        });*/
    }

    // find zipcode containing the coords
    findZip(latlng: LatLng): ?string {
        // before looping through all zipcodes, test currently zoomed zipcode, most of the time it will be correct
        if (this._selectedZipcodeFeatureData && d3.geoContains(this._selectedZipcodeFeatureData.feature, [latlng.lng, latlng.lat])) {
            return this._selectedZipcodeFeatureData.feature.properties.ZCTA5CE10;
        }

        // if we zoomed via city, no zip will be selected
        if (this.geoJsonLayer) {
            let found = null;
            this.geoJsonLayer.eachLayer(layer => {
                if (!found && d3.geoContains(layer.feature, [latlng.lng, latlng.lat])) {
                    found = layer.feature.properties.ZCTA5CE10;
                }
            });
            return found;
        }
    }

    highlight(target: LeafletClickTarget) {
        this.resetHighlight() // reset existing highlight, if any
        this._selectedZipcodeFeatureData = target

        let bounds = target.getBounds();
        target.setStyle(this.styleFeature(target.feature, true));
        target.getElement().style.animation = 'dash 100s linear infinite, glow-highlight 5s infinite'

        if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
            target.bringToFront();
        }
    }

    // resets selected zipcode region
    resetHighlight() {
        const target = this._selectedZipcodeFeatureData
        if (!this._selectedZipcodeFeatureData) {
            return
        }
        if (this.geoJsonLayer) {
            target.getElement().style.animation = ''
            target.setStyle(this.styleFeature(target.feature, false));
            // this.geoJsonLayer.resetStyle(target)
        }
        this._selectedZipcodeFeatureData = null
    }
}


export default CityMap;

